import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faChevronDown, faChevronUp, faArrowsAlt, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import type { WindowState } from './types';

export const HEADER_HEIGHT = 40;

interface WindowChromeProps {
  windowRef: React.RefObject<HTMLDivElement | null>;
  headerRef: React.RefObject<HTMLDivElement | null>;
  windowState: WindowState;
  draggable: boolean;
  resizable: boolean;
  showMinimizeButton: boolean;
  showCloseButton: boolean;
  windowTitle?: string;
  onWindowClick: (e: React.MouseEvent) => void;
  onHeaderMouseDown: (e: React.MouseEvent) => void;
  onHeaderTouchStart: (e: React.TouchEvent) => void;
  onResizeMouseDown: (e: React.MouseEvent) => void;
  onResizeTouchStart: (e: React.TouchEvent) => void;
  onMinimizeClick: (e: React.MouseEvent) => void;
  onCloseClick: (e: React.MouseEvent) => void;
  /** When provided, shows a reset button beside the title. Clears saved
   *  explorer state (sort, filters, widths) and reloads defaults. */
  onResetClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

export default function WindowChrome({
  windowRef,
  headerRef,
  windowState,
  draggable,
  resizable,
  showMinimizeButton,
  showCloseButton,
  windowTitle,
  onWindowClick,
  onHeaderMouseDown,
  onHeaderTouchStart,
  onResizeMouseDown,
  onResizeTouchStart,
  onMinimizeClick,
  onCloseClick,
  onResetClick,
  children,
}: WindowChromeProps) {
  // Minimize collapses the OUTER box only; the inner content keeps its
  // full size so Handsontable's viewport never shrinks. This preserves scroll
  // position reliably across minimize/restore cycles (display:none would
  // trigger HOT to re-measure from 0 and sometimes reset scroll to the top).
  const contentHeight = Math.max(0, windowState.size.height - HEADER_HEIGHT);

  const windowStyle: React.CSSProperties = {
    position: 'fixed',
    left: windowState.position.x,
    top: windowState.position.y,
    width: windowState.size.width,
    height: windowState.isMinimized ? HEADER_HEIGHT : windowState.size.height,
    zIndex: windowState.zIndex,
    backgroundColor: 'var(--em3-color-background-panel)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 8,
    boxShadow: 'var(--em3-shadow-xl)',
    overflow: 'hidden',
    transition: 'height 0.2s ease-in-out',
  };

  return (
    <div
      ref={windowRef}
      className="configurable-explorer-window"
      style={windowStyle}
      onClick={onWindowClick}
      tabIndex={-1}
    >
      <div
        ref={headerRef}
        className="window-header"
        onMouseDown={onHeaderMouseDown}
        onTouchStart={onHeaderTouchStart}
        style={{
          height: HEADER_HEIGHT,
          boxSizing: 'border-box',
          padding: '8px 12px',
          backgroundColor: 'var(--em3-navigation-bg)',
          borderBottom: '1px solid var(--em3-color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: draggable ? 'move' : 'default',
          userSelect: 'none',
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--em3-navigation-text)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{windowTitle || 'Explorer'}</span>
          {onResetClick && (
            <button
              onClick={(e) => { e.stopPropagation(); onResetClick(e); }}
              onMouseDown={(e) => e.stopPropagation()}
              style={headerButtonStyle}
              title="Reset layout (sort, filters, column widths)"
              aria-label="Reset explorer layout"
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--em3-color-background-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <FontAwesomeIcon icon={faRotateLeft} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {showMinimizeButton && (
            <button
              onClick={onMinimizeClick}
              style={headerButtonStyle}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--em3-color-background-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <FontAwesomeIcon icon={windowState.isMinimized ? faChevronUp : faChevronDown} />
            </button>
          )}
          {showCloseButton && (
            <button
              onClick={onCloseClick}
              style={headerButtonStyle}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--em3-color-danger)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
      </div>

      {/*
        Content keeps its full height (contentHeight) regardless of minimize
        state. When the outer collapses to HEADER_HEIGHT, the outer's
        overflow:hidden clips the content — but HOT inside sees a stable
        viewport, so its scroll position stays intact.
      */}
      <div
        className="window-content"
        style={{
          height: contentHeight,
          overflow: 'hidden',
          position: 'relative',
        }}
        onClick={onWindowClick}
      >
        {children}
      </div>

      {resizable && !windowState.isMinimized && (
        <div
          className="window-resize-handle"
          onMouseDown={onResizeMouseDown}
          onTouchStart={onResizeTouchStart}
          style={{
            position: 'absolute',
            bottom: 4,
            right: 4,
            width: 22,
            height: 22,
            cursor: 'nw-resize',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            borderRadius: '50%',
            backgroundColor: 'var(--em3-color-background-main)',
            border: '1px solid var(--em3-color-border)',
            boxShadow: 'var(--em3-shadow-sm)',
          }}
        >
          <FontAwesomeIcon
            icon={faArrowsAlt}
            style={{
              transform: 'rotate(90deg)',
              pointerEvents: 'none',
              fontSize: 11,
              color: 'var(--em3-color-text-primary)',
            }}
          />
        </div>
      )}
    </div>
  );
}

const headerButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--em3-navigation-text)',
  cursor: 'pointer',
  padding: 4,
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
};
